"use client";

import Image from "next/image";
import { useState } from "react";

import { MuxPlayer } from "@/components/post-rich-text/mux-player";
import { Dialog, DialogClose, DialogTitle } from "@/components/ui/dialog";
import {
  VideoDialogPopup,
  videoDialogCloseIconClass,
} from "@/components/ui/video-dialog";
import { cn } from "@/lib/utils";

const preloadMuxPlayer = () => {
  import("@mux/mux-player-react/lazy");
};

function muxThumbnailUrl(playbackId: string) {
  return `https://image.mux.com/${playbackId}/thumbnail.webp?fit_mode=smartcrop&time=0`;
}

interface CustomerStorySidebarVideoProps {
  playbackId: string;
  title: string;
  playLabel?: string;
  thumbnailSrc?: string;
  thumbnailAlt?: string;
  className?: string;
}

/** Sidebar video tile (Figma 4501:12212) — thumbnail opens a full-screen player. */
export function CustomerStorySidebarVideo({
  playbackId,
  title,
  playLabel = `Play video: ${title}`,
  thumbnailSrc,
  thumbnailAlt,
  className,
}: CustomerStorySidebarVideoProps) {
  const [open, setOpen] = useState(false);
  const poster = thumbnailSrc ?? muxThumbnailUrl(playbackId);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        onMouseEnter={preloadMuxPlayer}
        onTouchStart={preloadMuxPlayer}
        className={cn(
          "border-grey-100 group relative block aspect-[169/95] w-full overflow-hidden rounded-lg border text-left",
          className
        )}
        aria-label={playLabel}
      >
        <Image
          src={poster}
          alt={thumbnailAlt ?? title}
          fill
          sizes="(min-width: 1024px) 169px, 100vw"
          className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
        <PlayBadge className="absolute right-1.5 bottom-1.5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <VideoDialogPopup initialFocus={false}>
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <div className="rounded-container relative aspect-video overflow-hidden">
            <MuxPlayer
              playbackId={playbackId}
              title={title}
              autoPlay
              loading="page"
              preload="auto"
              renditionOrder="desc"
              minResolution="1080p"
              className="absolute inset-0"
            />
          </div>
          <DialogClose className={videoDialogCloseIconClass} />
        </VideoDialogPopup>
      </Dialog>
    </>
  );
}

function PlayBadge({ className }: { className?: string }) {
  return (
    <span className={className} aria-hidden style={{ display: "inline-flex" }}>
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="12" cy="12" r="12" fill="#0C0D0D" fillOpacity="0.6" />
        <path d="M10 8.5 16 12l-6 3.5v-7Z" fill="#fff" />
      </svg>
    </span>
  );
}
